import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const TYPE_LABELS = { p: "段落", h3: "小标题", list: "要点列表", qa: "问答" };

const listToText = (items, k) => (items || []).map((i) => i[k] || "").join("\n");
const textToList = (zhText, enText) => {
  const zl = zhText.split("\n");
  const el = enText.split("\n");
  const n = Math.max(zl.length, el.length);
  const out = [];
  for (let i = 0; i < n; i++) out.push({ zh: (zl[i] || "").trim(), en: (el[i] || "").trim() });
  return out.filter((i) => i.zh || i.en);
};
const qaToText = (items, k) => (items || []).map((i) => `${i.q?.[k] || ""} || ${i.a?.[k] || ""}`).join("\n");
const textToQa = (zhText, enText) => {
  const parse = (t) =>
    t.split("\n").map((l) => {
      const [q, ...rest] = l.split("||");
      return { q: (q || "").trim(), a: rest.join("||").trim() };
    });
  const zl = parse(zhText);
  const el = parse(enText);
  const n = Math.max(zl.length, el.length);
  const out = [];
  for (let i = 0; i < n; i++) out.push({ q: { zh: zl[i]?.q || "", en: el[i]?.q || "" }, a: { zh: zl[i]?.a || "", en: el[i]?.a || "" } });
  return out.filter((i) => i.q.zh || i.q.en);
};

const F = ({ label, value, onChange, multiline, rows = 3, testid }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</label>
    {multiline ? (
      <Textarea data-testid={testid} rows={rows} className="bg-white" value={value} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <Input data-testid={testid} className="bg-white" value={value} onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

export const FaqEditor = ({ draft, updateFaq }) => {
  const faq = draft.faq && draft.faq.sections ? draft.faq : { sections: [], closing: {}, disclaimer: {} };
  const setBlock = (si, bi, nb) => updateFaq((f) => { f.sections[si].blocks[bi] = nb; });

  const blockFields = (si, b, bi) => {
    if (b.t === "list") {
      return (
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="要点 中文（每行一条）" multiline rows={5} testid={`faq-${si}-${bi}-items-zh`} value={listToText(b.items, "zh")}
            onChange={(v) => setBlock(si, bi, { ...b, items: textToList(v, listToText(b.items, "en")) })} />
          <F label="要点 English（每行一条，与中文逐行对应）" multiline rows={5} testid={`faq-${si}-${bi}-items-en`} value={listToText(b.items, "en")}
            onChange={(v) => setBlock(si, bi, { ...b, items: textToList(listToText(b.items, "zh"), v) })} />
        </div>
      );
    }
    if (b.t === "qa") {
      return (
        <div className="space-y-2">
          <p className="text-xs text-slate-400">每行一条问答，格式：问题 || 回答（中英逐行对应）</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="问答 中文" multiline rows={6} testid={`faq-${si}-${bi}-qa-zh`} value={qaToText(b.items, "zh")}
              onChange={(v) => setBlock(si, bi, { ...b, items: textToQa(v, qaToText(b.items, "en")) })} />
            <F label="问答 English" multiline rows={6} testid={`faq-${si}-${bi}-qa-en`} value={qaToText(b.items, "en")}
              onChange={(v) => setBlock(si, bi, { ...b, items: textToQa(qaToText(b.items, "zh"), v) })} />
          </div>
        </div>
      );
    }
    return (
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="中文" multiline={b.t === "p"} testid={`faq-${si}-${bi}-zh`} value={b.zh || ""} onChange={(v) => setBlock(si, bi, { ...b, zh: v })} />
        <F label="English" multiline={b.t === "p"} testid={`faq-${si}-${bi}-en`} value={b.en || ""} onChange={(v) => setBlock(si, bi, { ...b, en: v })} />
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="faq-editor">
      {faq.sections.map((s, si) => (
        <Card key={si} className="rounded-3xl border-slate-200" data-testid={`faq-section-edit-${si}`}>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="font-display">章节 {si + 1} · {s.title?.zh || s.title?.en || "（未命名）"}</CardTitle>
            <Button data-testid={`faq-section-remove-${si}`} variant="ghost" size="sm" className="text-red-500 hover:text-red-600"
              onClick={() => updateFaq((f) => f.sections.splice(si, 1))}>
              <Trash2 size={15} />
            </Button>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="章节标题 中文" testid={`faq-${si}-title-zh`} value={s.title?.zh || ""} onChange={(v) => updateFaq((f) => { f.sections[si].title.zh = v; })} />
              <F label="章节标题 English" testid={`faq-${si}-title-en`} value={s.title?.en || ""} onChange={(v) => updateFaq((f) => { f.sections[si].title.en = v; })} />
            </div>
            {(s.blocks || []).map((b, bi) => (
              <div key={bi} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4 space-y-3" data-testid={`faq-block-${si}-${bi}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-sky-600 bg-sky-100 rounded-full px-2.5 py-0.5">{TYPE_LABELS[b.t] || b.t}</span>
                  <Button data-testid={`faq-block-remove-${si}-${bi}`} variant="ghost" size="sm" className="text-red-500 hover:text-red-600 h-7"
                    onClick={() => updateFaq((f) => f.sections[si].blocks.splice(bi, 1))}>
                    <Trash2 size={13} />
                  </Button>
                </div>
                {blockFields(si, b, bi)}
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              {[["p", "段落"], ["h3", "小标题"], ["list", "要点列表"], ["qa", "问答"]].map(([t, l]) => (
                <Button key={t} data-testid={`faq-block-add-${si}-${t}`} variant="outline" size="sm" className="rounded-full"
                  onClick={() => updateFaq((f) => {
                    f.sections[si].blocks.push(t === "list" || t === "qa" ? { t, items: [] } : { t, zh: "", en: "" });
                  })}>
                  <Plus size={13} className="mr-1" /> {l}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
      <Button data-testid="faq-section-add" variant="outline" className="rounded-full"
        onClick={() => updateFaq((f) => f.sections.push({ title: { zh: "", en: "" }, blocks: [] }))}>
        <Plus size={15} className="mr-1" /> 添加章节
      </Button>

      <Card className="rounded-3xl border-slate-200">
        <CardHeader><CardTitle className="font-display">结尾品牌区</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {[["h", "标题"], ["p1", "第一段"], ["p2", "核心问题"], ["p3", "收尾句"]].map(([k, l]) => (
            <div key={k} className="grid sm:grid-cols-2 gap-4">
              <F label={`${l} 中文`} testid={`faq-closing-${k}-zh`} value={faq.closing?.[`${k}_zh`] || ""}
                onChange={(v) => updateFaq((f) => { f.closing[`${k}_zh`] = v; })} />
              <F label={`${l} English`} testid={`faq-closing-${k}-en`} value={faq.closing?.[`${k}_en`] || ""}
                onChange={(v) => updateFaq((f) => { f.closing[`${k}_en`] = v; })} />
            </div>
          ))}
          <F label="品牌口号（英文）" testid="faq-closing-motto" value={faq.closing?.motto_en || ""}
            onChange={(v) => updateFaq((f) => { f.closing.motto_en = v; })} />
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-slate-200">
        <CardHeader><CardTitle className="font-display">免责声明</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <F label="标题" testid="faq-disclaimer-h" value={faq.disclaimer?.h || ""}
            onChange={(v) => updateFaq((f) => { f.disclaimer.h = v; })} />
          <F label="English" multiline rows={4} testid="faq-disclaimer-en" value={faq.disclaimer?.en || ""}
            onChange={(v) => updateFaq((f) => { f.disclaimer.en = v; })} />
          <F label="中文" multiline rows={4} testid="faq-disclaimer-zh" value={faq.disclaimer?.zh || ""}
            onChange={(v) => updateFaq((f) => { f.disclaimer.zh = v; })} />
        </CardContent>
      </Card>
    </div>
  );
};
